import { useState, useEffect } from "react";

const ManualAddressEntry = ({ onSubmit, onCancel, initialQuery = "" }) => {
    // US States with full names and abbreviations
    const stateOptions = [
        { value: "", label: "Select State" },
        { value: "AL", label: "Alabama (AL)" },
        { value: "AK", label: "Alaska (AK)" },
        { value: "AZ", label: "Arizona (AZ)" },
        { value: "AR", label: "Arkansas (AR)" },
        { value: "CA", label: "California (CA)" },
        { value: "CO", label: "Colorado (CO)" },
        { value: "CT", label: "Connecticut (CT)" },
        { value: "DE", label: "Delaware (DE)" },
        { value: "FL", label: "Florida (FL)" },
        { value: "GA", label: "Georgia (GA)" },
        { value: "HI", label: "Hawaii (HI)" },
        { value: "ID", label: "Idaho (ID)" },
        { value: "IL", label: "Illinois (IL)" },
        { value: "IN", label: "Indiana (IN)" },
        { value: "IA", label: "Iowa (IA)" },
        { value: "KS", label: "Kansas (KS)" },
        { value: "KY", label: "Kentucky (KY)" },
        { value: "LA", label: "Louisiana (LA)" },
        { value: "ME", label: "Maine (ME)" },
        { value: "MD", label: "Maryland (MD)" },
        { value: "MA", label: "Massachusetts (MA)" },
        { value: "MI", label: "Michigan (MI)" },
        { value: "MN", label: "Minnesota (MN)" },
        { value: "MS", label: "Mississippi (MS)" },
        { value: "MO", label: "Missouri (MO)" },
        { value: "MT", label: "Montana (MT)" },
        { value: "NE", label: "Nebraska (NE)" },
        { value: "NV", label: "Nevada (NV)" },
        { value: "NH", label: "New Hampshire (NH)" },
        { value: "NJ", label: "New Jersey (NJ)" },
        { value: "NM", label: "New Mexico (NM)" },
        { value: "NY", label: "New York (NY)" },
        { value: "NC", label: "North Carolina (NC)" },
        { value: "ND", label: "North Dakota (ND)" },
        { value: "OH", label: "Ohio (OH)" },
        { value: "OK", label: "Oklahoma (OK)" },
        { value: "OR", label: "Oregon (OR)" },
        { value: "PA", label: "Pennsylvania (PA)" },
        { value: "RI", label: "Rhode Island (RI)" },
        { value: "SC", label: "South Carolina (SC)" },
        { value: "SD", label: "South Dakota (SD)" },
        { value: "TN", label: "Tennessee (TN)" },
        { value: "TX", label: "Texas (TX)" },
        { value: "UT", label: "Utah (UT)" },
        { value: "VT", label: "Vermont (VT)" },
        { value: "VA", label: "Virginia (VA)" },
        { value: "WA", label: "Washington (WA)" },
        { value: "WV", label: "West Virginia (WV)" },
        { value: "WI", label: "Wisconsin (WI)" },
        { value: "WY", label: "Wyoming (WY)" },
        { value: "DC", label: "Washington D.C. (DC)" },
        { value: "AS", label: "American Samoa (AS)" },
        { value: "GU", label: "Guam (GU)" },
        { value: "MP", label: "Northern Mariana Islands (MP)" },
        { value: "PR", label: "Puerto Rico (PR)" },
        { value: "VI", label: "U.S. Virgin Islands (VI)" },
    ];

    const [formData, setFormData] = useState({
        streetNumber: "",
        streetName: "",
        city: "",
        state: "",
        zipCode: "",
    });
    const [errors, setErrors] = useState({});

    // Parse initial query if provided
    useEffect(() => {
        if (initialQuery) {
            const parts = initialQuery.split(",").map((part) => part.trim());
            if (parts.length >= 1) {
                const streetParts = parts[0].split(" ");
                if (streetParts.length >= 2 && /^\d+/.test(streetParts[0])) {
                    setFormData((prev) => ({
                        ...prev,
                        streetNumber: streetParts[0],
                        streetName: streetParts.slice(1).join(" "),
                        city: parts[1] || "",
                        state: parts[2] || "",
                        zipCode: parts[3] || "",
                    }));
                } else {
                    setFormData((prev) => ({
                        ...prev,
                        streetName: parts[0] || "",
                        city: parts[1] || "",
                        state: parts[2] || "",
                        zipCode: parts[3] || "",
                    }));
                }
            }
        }
    }, [initialQuery]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.streetName.trim()) {
            newErrors.streetName = "Street name is required";
        }

        if (!formData.city.trim()) {
            newErrors.city = "City is required";
        }

        if (!formData.state.trim()) {
            newErrors.state = "State is required";
        }

        // Street number validation
        if (formData.streetNumber && !/^\d+[A-Za-z]?$/.test(formData.streetNumber.trim())) {
            newErrors.streetNumber = "Please enter a valid street number (e.g., 123, 123A)";
        }

        // Basic US ZIP code validation
        if (formData.zipCode && !/^\d{5}(-\d{4})?$/.test(formData.zipCode.trim())) {
            newErrors.zipCode = "Please enter a valid ZIP code (12345 or 12345-6789)";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        if (!validateForm()) {
            return;
        }

        // Format the address
        const fullStreet = formData.streetNumber.trim()
            ? `${formData.streetNumber.trim()} ${formData.streetName.trim()}`
            : formData.streetName.trim();

        const formattedAddress = [fullStreet, formData.city.trim(), formData.state.trim(), formData.zipCode.trim(), "United States"]
            .filter((part) => part)
            .join(", ");

        // Create address components for compatibility
        const addressComponents = [];

        if (formData.streetNumber.trim()) {
            addressComponents.push({
                types: ["street_number"],
                long_name: formData.streetNumber.trim(),
                short_name: formData.streetNumber.trim(),
            });
        }

        if (formData.streetName.trim()) {
            addressComponents.push({
                types: ["route"],
                long_name: formData.streetName.trim(),
                short_name: formData.streetName.trim(),
            });
        }

        if (formData.city.trim()) {
            addressComponents.push({
                types: ["locality"],
                long_name: formData.city.trim(),
                short_name: formData.city.trim(),
            });
        }

        if (formData.state.trim()) {
            addressComponents.push({
                types: ["administrative_area_level_1"],
                long_name: formData.state.trim(),
                short_name: formData.state.trim(),
            });
        }

        if (formData.zipCode.trim()) {
            addressComponents.push({
                types: ["postal_code"],
                long_name: formData.zipCode.trim(),
                short_name: formData.zipCode.trim(),
            });
        }

        // Add country
        addressComponents.push({
            types: ["country"],
            long_name: "United States",
            short_name: "US",
        });

        const manualAddress = {
            formatted_address: formattedAddress,
            address_components: addressComponents,
            latitude: 0, // Default coordinates - could be enhanced with geocoding later
            longitude: 0,
            types: ["street_address"],
            manual_entry: true,
        };

        onSubmit(manualAddress);
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
            <div className="tw-bg-white tw-rounded-lg tw-p-6 tw-w-full tw-max-w-md tw-mx-4">
                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Enter Address Manually</h3>

                <form onSubmit={handleSubmit} className="tw-space-y-4">
                    <div className="tw-grid tw-grid-cols-4 tw-gap-2">
                        <div className="tw-col-span-1">
                            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Number</label>
                            <input
                                type="text"
                                value={formData.streetNumber}
                                onChange={(e) => handleInputChange("streetNumber", e.target.value)}
                                placeholder="123"
                                maxLength="10"
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-primary ${
                                    errors.streetNumber ? "tw-border-red-500" : "tw-border-gray-300"
                                }`}
                            />
                            {errors.streetNumber && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.streetNumber}</p>}
                        </div>
                        <div className="tw-col-span-3">
                            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Street Name *</label>
                            <input
                                type="text"
                                value={formData.streetName}
                                onChange={(e) => handleInputChange("streetName", e.target.value)}
                                placeholder="Main Street"
                                maxLength="100"
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-primary ${
                                    errors.streetName ? "tw-border-red-500" : "tw-border-gray-300"
                                }`}
                            />
                            {errors.streetName && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.streetName}</p>}
                        </div>
                    </div>

                    <div>
                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">City *</label>
                        <input
                            type="text"
                            value={formData.city}
                            onChange={(e) => handleInputChange("city", e.target.value)}
                            placeholder="New York"
                            maxLength="50"
                            className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-primary ${
                                errors.city ? "tw-border-red-500" : "tw-border-gray-300"
                            }`}
                        />
                        {errors.city && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.city}</p>}
                    </div>

                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                        <div>
                            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">State *</label>
                            <select
                                value={formData.state}
                                onChange={(e) => handleInputChange("state", e.target.value)}
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-primary tw-bg-white tw-cursor-pointer ${
                                    errors.state ? "tw-border-red-500" : "tw-border-gray-300"
                                }`}
                            >
                                {stateOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {errors.state && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.state}</p>}
                        </div>
                        <div>
                            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">ZIP Code</label>
                            <input
                                type="text"
                                value={formData.zipCode}
                                onChange={(e) => handleInputChange("zipCode", e.target.value)}
                                placeholder="10001"
                                maxLength="10"
                                className={`tw-w-full tw-px-3 tw-py-2 tw-border tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-border-primary ${
                                    errors.zipCode ? "tw-border-red-500" : "tw-border-gray-300"
                                }`}
                            />
                            {errors.zipCode && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.zipCode}</p>}
                        </div>
                    </div>

                    <div className="tw-flex tw-justify-end tw-space-x-3 tw-pt-4">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="tw-px-4 tw-py-2 tw-text-gray-700 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-md tw-transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="tw-px-4 tw-py-2 tw-bg-primary hover:tw-bg-primary_hover tw-text-white tw-rounded-md tw-transition-colors"
                        >
                            Use This Address
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ManualAddressEntry;
